import { assign, createMachine, DoneActorEvent, fromPromise } from "xstate";
import {
  FastCheckoutMachineContext,
  FastCheckoutMachineEvents,
  FastCheckoutState,
  FastCheckoutSubmitUserInteractionEvent,
} from "./types/FastCheckoutMachine";
import {
  getErrorTypeFromCheckoutOrder,
  getActiveStageFromCheckoutSection,
  getSectionStateFromCheckoutOrder,
} from "../utils";
import {
  UserInteractionType,
  UserRetailerCheckoutOrder,
  UserRetailerCheckoutOrderStatusV2,
} from "../types";
import {
  getRetailerCheckoutOrderStatusV2,
  getRetailerCheckoutOrderStatusV2Mock,
  reSubmitCheckout,
  RetailerCheckoutOrderStatusV2,
  submitUserInteraction,
  submitUserInteractionMock,
} from "@/api/rest/checkoutApi";

export type FastCheckoutMachineType = typeof fastCheckoutMachine;
export const MOCKING_CHECKOUT = false;

// Poll interval in milliseconds
const POLL_INTERVAL = 3000;

const retryCheckoutActor = fromPromise(
  async ({
    input,
  }: {
    input: {
      context: FastCheckoutMachineContext;
      event: FastCheckoutMachineEvents;
    };
  }) => {
    const { orderId } = input.context;
    if (!orderId) {
      throw new Error("No order ID provided for polling");
    }

    const res = await reSubmitCheckout({ orderId });
    return res;
  }
);

// Actor for polling checkout status
const pollCheckoutStatusActor = fromPromise(
  async ({
    input: { context, event },
  }: {
    input: {
      context: FastCheckoutMachineContext;
      event: FastCheckoutMachineEvents;
    };
  }) => {
    try {
      console.log(
        "🔄 pollCheckoutStatusActor - Starting poll with input:",
        context.orderId
      );
      const { orderId } = context;
      if (!orderId) {
        throw new Error("No order ID provided for polling");
      }

      const res = MOCKING_CHECKOUT
        ? await getRetailerCheckoutOrderStatusV2Mock({ orderId })
        : await getRetailerCheckoutOrderStatusV2({ orderId });

      return res;
    } catch (e) {
      throw e;
    }
  }
);

const submitUserInteractionApi = fromPromise(
  async ({
    input,
  }: {
    input: {
      context: FastCheckoutMachineContext;
      event: FastCheckoutMachineEvents;
    };
  }) => {
    const order =
      input.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder;
    const communicationId = order?.userInteractionRequirement?.communicationId;
    const orderId = order?.orderId;

    const request = (input.event as FastCheckoutSubmitUserInteractionEvent).data
      .interactionData;
    console.log("🔄 submitUserInteractionApi - Request:", request);

    if (!orderId || !communicationId) {
      throw new Error("No order ID or communication ID provided");
    }

    const res = MOCKING_CHECKOUT
      ? await submitUserInteractionMock(orderId, communicationId, {
          response: request,
        })
      : await submitUserInteraction(orderId, communicationId, {
          response: request,
        });
    return res;
  }
);

export const fastCheckoutMachine = (() => {
  const machineId = "fastCheckoutMachine";
  return createMachine(
    {
      id: machineId,
      initial: "polling",
      types: {} as unknown as {
        context: FastCheckoutMachineContext;
        events: FastCheckoutMachineEvents;
      },
      on: {
        ON_SESSION_OPEN: {
          actions: ["openSection"],
        },
        ON_SESSION_CLOSE: {
          actions: ["closeSection"],
        },
      },
      context: ({
        input: { context },
      }: {
        input: { context: FastCheckoutMachineContext };
      }) => context,
      states: {
        waiting: {
          entry: [
            assign({
              checkoutState: FastCheckoutState.LOADING,
              activeStage: 1,
            }),
          ],
        },
        polling: {
          invoke: {
            src: "pollCheckoutStatus",
            input: ({ context, event }) => ({
              context,
              event,
            }),
            onDone: [
              {
                target: "#fastCheckoutMachine.interactionTimeout",
                guard: "isOrderTimeout",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "receiptGenerated",
                guard: "isCheckoutReceiptGeneratedStage",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "processing.actionRequiredError",
                guard: "isActionRequiredErrorStage",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "processing.nonActionRequiredError",
                guard: "isCheckoutNonActionRequiredErrorStage",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "pollAgain",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
            ],
          },
        },
        pollAgain: {
          after: {
            [POLL_INTERVAL]: {
              target: "polling",
              guard: "shouldContinuePolling",
            },
          },
        },
        processing: {
          initial: "noError",
          entry: [
            assign({
              checkoutState: FastCheckoutState.PROCESSING,
            }),
          ],
          states: {
            noError: {},
            actionRequiredError: {
              entry: [
                assign({
                  checkoutState: FastCheckoutState.ACTION_REQUIRED_ERROR,
                }),
              ],
              initial: "polling",
              on: {
                SUBMIT_USER_INTERACTION: {
                  target: "interactionProcessing",
                },
              },
              // poll response to see if the user interaction is successful
              // if not, keep polling
              states: {
                polling: {
                  invoke: {
                    input: ({ context, event }) => ({
                      context,
                      event,
                    }),
                    src: "pollCheckoutStatus",
                    onDone: [
                      {
                        target: "pollAgain",
                        guard: "isUserInteractionRequired",
                      },
                      {
                        target: "#fastCheckoutMachine.polling",
                      },
                    ],
                    onError: [
                      {
                        target: "#fastCheckoutMachine.polling",
                      },
                    ],
                  },
                },
                pollAgain: {
                  after: {
                    [POLL_INTERVAL]: {
                      target: "polling",
                    },
                  },
                },
              },
            },
            interactionProcessing: {
              invoke: {
                input: ({ context, event }) => ({
                  context,
                  event,
                }),
                src: "submitUserInteractionService",
                onDone: [
                  {
                    target: "#fastCheckoutMachine.polling",
                    actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
                  },
                ],
                onError: [
                  {
                    target: "#fastCheckoutMachine.polling",
                  },
                ],
              },
            },
            nonActionRequiredError: {
              entry: assign({
                checkoutState: FastCheckoutState.NON_ACTION_REQUIRED_ERROR,
              }),
            },
          },
        },
        interactionTimeout: {
          entry: [],
          initial: "waitingForRetry",
          on: {
            ON_RETRY_CHECKOUT: {
              target: "#fastCheckoutMachine.interactionTimeout.retryCheckout",
            },
          },
          states: {
            waitingForRetry: {},
            retryCheckout: {
              invoke: {
                input: ({ context, event }) => ({
                  context,
                  event,
                }),
                src: "retryCheckout",
                onDone: [
                  {
                    target: "#fastCheckoutMachine.polling",
                    actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
                  },
                ],
              },
            },
          },
        },
        receiptGenerated: {
          entry: assign({
            checkoutState: FastCheckoutState.RECEIPT_GENERATED,
          }),
        },
      },
    },
    {
      actors: {
        submitUserInteractionService: submitUserInteractionApi,
        pollCheckoutStatus: pollCheckoutStatusActor,
        retryCheckout: retryCheckoutActor,
      },
      actions: {
        openSection: assign({
          sectionState: ({ event, context }) =>
            event?.type === "ON_SESSION_OPEN"
              ? {
                  ...context.sectionState,
                  [event.data.sessionToOpen]: {
                    ...context.sectionState[event.data.sessionToOpen],
                    open: true,
                  },
                }
              : context.sectionState,
        }),
        closeSection: assign({
          sectionState: ({ event, context }) =>
            event?.type === "ON_SESSION_CLOSE"
              ? {
                  ...context.sectionState,
                  [event.data.sessionToClose]: {
                    ...context.sectionState[event.data.sessionToClose],
                    open: false,
                  },
                }
              : context.sectionState,
        }),
        updateRetailerCheckoutOrderStatusFromPoller: assign(
          ({
            event,
            context,
          }: {
            event: FastCheckoutMachineEvents;
            context: FastCheckoutMachineContext;
          }) => {
            const eventData =
              event as DoneActorEvent<RetailerCheckoutOrderStatusV2>;
            const products = eventData.output?.products;
            const statusData = eventData.output;
            const order = statusData?.userRetailerCheckoutOrder;
            const displayStage = statusData?.displayStatusInfo?.stage;
            const errorType = getErrorTypeFromCheckoutOrder(order);
            const stage = getActiveStageFromCheckoutSection(displayStage);

            return {
              products,
              sectionState: getSectionStateFromCheckoutOrder(
                context.sectionState,
                order
              ),
              checkoutState: errorType
                ? FastCheckoutState.ACTION_REQUIRED_ERROR
                : FastCheckoutState.PROCESSING,
              retailerCheckoutOrderStatus: statusData,
              activeStage: getActiveStageFromCheckoutSection(displayStage),
              error: !errorType
                ? undefined
                : {
                    stage,
                    type: errorType,
                    interactionRequired: order?.userInteractionRequirement,
                    errorMessage:
                      order?.userInteractionRequirement?.displayMessage,
                  },
            };
          }
        ),

        stopPolling: () => {
          // This is a no-op action that just signals the polling should stop
        },
      },
      guards: {
        isUserInteractionRequired: ({ event }) => {
          if (!("output" in event)) return false;

          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;

          return !!userRetailerCheckoutOrder.userInteractionRequirement?.type;
        },
        isOrderTimeout: ({ event }) => {
          if (!("output" in event)) return false;

          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;

          return (
            userRetailerCheckoutOrder.status ===
            UserRetailerCheckoutOrderStatusV2.user_interaction_timeout
          );
        },
        isCheckoutNonActionRequiredErrorStage: () => {
          return false;
        },
        isCheckoutReceiptGeneratedStage: ({ event }) => {
          if (!("output" in event)) return false;
          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;
          return (
            userRetailerCheckoutOrder.status ===
              UserRetailerCheckoutOrderStatusV2.checkout_cancelled ||
            userRetailerCheckoutOrder.status ===
              UserRetailerCheckoutOrderStatusV2.checkout_error ||
            userRetailerCheckoutOrder.status ===
              UserRetailerCheckoutOrderStatusV2.checkout_success
          );
        },
        isActionRequiredErrorStage: ({ event }) => {
          if (!("output" in event)) return false;

          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;

          const type =
            userRetailerCheckoutOrder.userInteractionRequirement?.type;
          return !!type;
        },
        shouldContinuePolling: ({ context }) => {
          const order =
            context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder;

          const shouldContinue =
            context.checkoutState === FastCheckoutState.PROCESSING ||
            (context.checkoutState ===
              FastCheckoutState.ACTION_REQUIRED_ERROR &&
              order?.userInteractionRequirement?.type ===
                UserInteractionType.OTP_VERIFICATION);

          console.log(
            "🔄 shouldContinuePolling - State:",
            context.checkoutState,
            "Continue:",
            shouldContinue
          );
          return shouldContinue;
        },
      },
    }
  );
})();
